import { cn, ToggleGroup } from "@aircall/ds";
import {
  Children,
  type ComponentPropsWithoutRef,
  cloneElement,
  isValidElement,
  type Key,
  type ReactElement,
  useEffect,
} from "react";

import { useControlled } from "@/hooks/useControlled/useControlled";

import type { InputChoiceProps } from "./components/InputChoice/InputChoice";
import { InputChoicesContextProvider } from "./contexts/InputChoicesContext/InputChoicesContext";

export type Mode = "binary" | "multiple";

export interface InputChoicesProps<Value extends Key>
  extends Omit<
    ComponentPropsWithoutRef<typeof ToggleGroup>,
    "onChange" | "onValueChange" | "value" | "defaultValue" | "children"
  > {
  mode: Mode;
  children: ReactElement<any> | ReactElement<any>[];
  value?: Value | null;
  onChange?: (value: Value | null) => void;
  "data-test"?: string;
}

/**
 * Segmented single-select control composed on the DS `ToggleGroup`. The
 * consumer assembles the compound: an `InputChoices` root wrapping
 * `InputChoice` items (each holding an optional `InputChoiceIcon` /
 * `InputChoiceLabel`).
 *
 * Adapts the scalar `value | null` public API to/from the DS ToggleGroup's
 * array-of-pressed-values shape, and injects each child's positional `index`
 * so a choice can resolve its tone without fragile `first:`/`last:` variants.
 */
export function InputChoices<Value extends Key = Key>({
  children,
  value,
  onChange,
  mode,
  "data-test": dataTest = "input-choices-wrapper",
  className,
  ...props
}: InputChoices.Props<Value>) {
  const [inputValue, setInputValue] = useControlled(value, {
    defaultValue: null,
    onChange,
  });

  // Validate that we have exactly 2 children if mode is binary.
  useEffect(() => {
    if (Children.count(children) !== 2 && mode === "binary") {
      throw new Error(
        "If mode is binary, InputChoices must have exactly 2 children."
      );
    }
  }, [mode, children]);

  // Inject the positional index so each choice can resolve its tone (first ->
  // positive/green, last -> negative/red in binary mode) without relying on
  // fragile `first:`/`last:` CSS variants.
  const indexedChildren = Children.map(children, (child, index) =>
    isValidElement<InputChoiceProps<Value>>(child)
      ? cloneElement(child, { index })
      : child
  );

  return (
    <InputChoicesContextProvider
      value={inputValue}
      onChange={setInputValue}
      mode={mode}
    >
      <ToggleGroup
        data-test={dataTest}
        // DS ToggleGroup is single-select (`multiple` defaults to false) and
        // models its value as an array of pressed values. Adapt our scalar
        // `value | null` public API to/from that array shape.
        value={inputValue != null ? [String(inputValue)] : []}
        onValueChange={(groupValue) => {
          // Preserve the legacy behaviour: clicking the already-selected choice
          // must not blank the field. Only react to a positive selection.
          if (groupValue.length > 0) {
            setInputValue(groupValue[0] as Value);
          }
        }}
        block
        className={cn(
          "min-w-min overflow-hidden rounded-lg border border-border",
          className
        )}
        {...props}
      >
        {indexedChildren}
      </ToggleGroup>
    </InputChoicesContextProvider>
  );
}

export namespace InputChoices {
  export type Props<Value extends Key> = InputChoicesProps<Value>;
}
